/**
 * Hook de tema (claro/escuro/auto).
 *
 * - `auto` (default): segue a preferência do sistema operacional
 *   (`prefers-color-scheme`). Se o usuário troca o modo no Mac/Windows,
 *   o painel acompanha em tempo real.
 * - `light`: força modo claro independente do SO.
 * - `dark`: força modo escuro.
 *
 * A escolha é salva em localStorage por usuário/navegador. Cada cliente
 * (tenant) tem a própria preferência.
 */
import { useEffect, useState, useCallback } from 'react'

export type ThemeMode = 'auto' | 'light' | 'dark'

const STORAGE_KEY = 'eventpro_theme_mode'

function carregar(): ThemeMode {
  if (typeof window === 'undefined') return 'auto'
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'auto') return v
  } catch { /* ignora */ }
  return 'auto'
}

function temaEfetivo(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light') return 'light'
  if (mode === 'dark') return 'dark'
  // Auto — segue o SO
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function aplicarNoHtml(efetivo: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  if (efetivo === 'light') document.documentElement.classList.add('theme-light')
  else document.documentElement.classList.remove('theme-light')
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(carregar)
  const [efetivo, setEfetivo] = useState<'light' | 'dark'>(() => temaEfetivo(carregar()))

  // Aplica no <html> quando muda + escuta mudança do SO se estiver em auto
  useEffect(() => {
    const novo = temaEfetivo(mode)
    setEfetivo(novo)
    aplicarNoHtml(novo)

    if (mode !== 'auto') return
    // Em auto: escuta o sistema operacional
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => {
      const e = temaEfetivo('auto')
      setEfetivo(e)
      aplicarNoHtml(e)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [mode])

  const setMode = useCallback((novo: ThemeMode) => {
    try { localStorage.setItem(STORAGE_KEY, novo) } catch { /* ignora */ }
    setModeState(novo)
  }, [])

  return { mode, setMode, efetivo }
}

/**
 * Inicializa o tema o mais cedo possível (chamado no main.tsx).
 * Evita "flash de tema errado" ao carregar a página.
 */
export function initTheme() {
  if (typeof window === 'undefined') return
  const mode = carregar()
  aplicarNoHtml(temaEfetivo(mode))
}
